// src/components/WorkedExampleReveal.jsx
//
// One worked-example card for the skills reader (TechniqueSection examples[] /
// AnalysisSection workedExample[]), upgraded from show-everything to ATTEMPT THEN
// REVEAL: the prompt stays visible (rendered by <RichText>, 5b -- markdown + glossary terms), the model answer
// hides behind a one-tap confidence row ("Not yet / Roughly / Confident" -- any tap
// reveals; a plain "Reveal model answer" works too). Confidence + reveal are
// EPHEMERAL local React state: per example, never persisted, no schema involved.
// Card wrapper + prompt/model <p> markup match the sections' previous inline render
// exactly, so styling and glossary popovers are unchanged after reveal.
//
// Keyboard/SR: the reveal buttons unmount on activation (one-shot, not a toggling
// disclosure -- so no aria-expanded/aria-controls, which would never be observable
// in the expanded state); instead focus MOVES to the revealed model region
// (tabIndex -1 + focus()), so focus is never dropped on <body> and the model is
// announced.
import React, { useState, useEffect, useRef } from 'react';
import RichText from './richText/RichText.jsx';
import { CONFIDENCE_LEVELS, exampleView } from './workedExampleView.js';

export default function WorkedExampleReveal({ prompt, model }) {
    const [revealed, setRevealed] = useState(false);
    const [confidence, setConfidence] = useState(null);
    const modelRef = useRef(null);
    const view = exampleView({ prompt, model, revealed });
    const reveal = (level) => { setConfidence(level); setRevealed(true); };

    // Move focus into the freshly revealed model so keyboard/screen-reader users
    // land on the answer instead of silently losing focus when the button unmounts.
    useEffect(() => {
        if (revealed && modelRef.current) modelRef.current.focus();
    }, [revealed]);

    if (!view.showCard) return null;

    return (
        <div className="border border-book-accent/20 bg-book-pageDark/40 p-5">
            {view.showPrompt && (
                <RichText text={prompt} className="font-serif italic text-book-muted mb-2" />
            )}
            {view.showConfidenceRow && (
                <div className={view.showPrompt ? 'mt-3 pt-3 border-t border-book-accent/15' : undefined}>
                    <div className="font-display text-book-muted text-xs mb-2 font-semibold">
                        Try it first &mdash; how confident are you?
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {CONFIDENCE_LEVELS.map((level) => (
                            <button key={level} type="button" onClick={() => reveal(level)}
                                className="px-3 py-1.5 border border-book-accent/40 text-book-accent font-display text-sm hover:bg-book-accent/10 transition-colors font-medium rounded-full">
                                {level}
                            </button>
                        ))}
                        <button type="button" onClick={() => reveal(null)}
                            className="px-2 py-1.5 font-serif italic text-xs text-book-muted hover:text-book-accent transition-colors">
                            Reveal model answer
                        </button>
                    </div>
                </div>
            )}
            {view.showModel && (
                <div ref={modelRef} tabIndex={-1} className="focus:outline-none" aria-label="Model answer">
                    {confidence && (
                        <div className="font-display text-book-muted/70 text-xs mb-1.5 font-semibold">
                            Your confidence: {confidence}
                        </div>
                    )}
                    <RichText text={model} className="font-serif text-book-text leading-relaxed" />
                </div>
            )}
        </div>
    );
}
